/* eslint-disable no-plusplus */
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { Button, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import useEU from '../../../../ciatEU/hook';
import Signer from '../auth/signer';
import api from '../../../../api/req';
import { CiatAppContext, WinManagerContext } from '../../../../providers';
import IconAlert from '../../../../components/blanks/common/IconAlert';
import { PORTRAIT } from '../../../../constants/pageLayouts';

const backendURL = '/api/akt_dvk/printform/akt_dvk/form_akt_dvk/';

function SignFileData({
  id, setModalSign, repr,
}) {
  const {
    readkeyFile, initialized, cert, err, loading,
    setErr, setLoading, onClearKey, libraryType, setLibraryType, keyMedias,
    readKeyMedia, selectedKM, setSelectedKM,
    PDFSignData,
    certificates, needCertificate, setCertificates, issuers, issuer, setIssuer,
  } = useEU();

  const { auth } = useContext(CiatAppContext);
  const navigate = useNavigate();
  const { dellComponentFromWindowsManager } = useContext(WinManagerContext);

  const generate = useCallback(
    async (format = 'html', allHtml = false) => {
      const params = {
        instance_id: id,
        to_format: format,
        all_html: allHtml,
        page_layout: PORTRAIT,
        cert,
      };

      const r = await api.post(backendURL, auth, params);
      if (!r.ok && r.status !== 400) throw new Error(`${r.status} ${r.statusText}`);
      if (!r.ok) {
        const fe = await r.json();
        setErr(fe);
        if ('error_data' in fe) {
          const messages = fe.error_data.reduce((R, i) => [...R, ...i.messages], []).join('\n');
          throw new Error(messages);
        }
        if (Array.isArray(fe)) {
          throw new Error(fe.join(', '));
        }
        const e = Object.keys(fe).reduce((R, rr) => `${R} \n ${rr}: ${fe[rr]}`);
        throw new Error(e);
      }
      return r.json();
    },
    [auth, cert, id, setErr],
  );

  const saveToDB = useCallback((filesSign, uid) => {
    const params = {
      files_sign: filesSign,
      uid,
      cert,
    };

    const sendToDB = async () => {
      const r = await api.post(`/api/akt_dvk/sign/${id}/`, auth, params);
      if (!r.ok) {
        if (r.status === 400) {
          const d = await r.json();
          if (d.error) throw new Error(d.error);
        }
        throw new Error(`${r.status} ${r.statusText}`);
      }
    };

    setLoading(true);

    sendToDB(params).then(() => {
      setLoading(false);
      setModalSign(false);
      toast.success(`${repr} підписано вдало`, {
        theme: 'colored',
      });
      const docUrl = `/doc/aktDvk/${id}/`;
      dellComponentFromWindowsManager(docUrl);
      navigate(docUrl);
    })
      .catch((e) => setErr(e.message.split('\n').filter((es) => !!es.trim())));
  }, [auth, cert, dellComponentFromWindowsManager,
    id, navigate, repr, setErr, setLoading, setModalSign]);

  const onSign = useCallback(() => {
    setLoading(true);
    setErr(null);

    generate('pdf_QRCode')
      .then(async (d) => {
        const binaryString = atob(d.result.pdf);
        const len = binaryString.length;
        const pdfBytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          pdfBytes[i] = binaryString.charCodeAt(i);
        }

        const signRaw = await PDFSignData(pdfBytes);

        saveToDB(signRaw, d.result.uid);
      })
      .catch((e) => setErr(e))
      .finally(() => setLoading(false));
  }, [setLoading, setErr, generate, PDFSignData, saveToDB]);

  return (
    <>
      {err && (
        <IconAlert variant={err.type}>
          {err.message}
          {err.url && (
          <>
            <hr />
            <Button href={err.url} variant={err.type} target="_blank">
              Завантажити
            </Button>
          </>
          )}
        </IconAlert>
      )}
      <Container>
        <h3 className="text-primary">
          <FontAwesomeIcon icon={faLock} className="me-2" />
          Підпис даних
        </h3>
        <Signer
          submitTitle="Підписати"
          loading={loading}
          err={err}
          initialized={initialized}
          onReadKeyFile={readkeyFile}
          onReadKeyMedia={readKeyMedia}
          cert={cert}
          onSubmit={onSign}
          onReset={onClearKey}
          libraryType={libraryType}
          onSetLibraryType={setLibraryType}
          keyMedias={keyMedias}
          selectedKM={selectedKM}
          setSelectedKM={setSelectedKM}
          certificates={certificates}
          needCertificate={needCertificate}
          setCertificates={setCertificates}
          setIssuer={setIssuer}
          issuers={issuers}
          issuer={issuer}
        />
      </Container>
    </>
  );
}

SignFileData.propTypes = {
  id: PropTypes.number.isRequired,
  setModalSign: PropTypes.func.isRequired,
  repr: PropTypes.string.isRequired,
};

export default SignFileData;
